import '@fontsource/poppins';
import axios from 'axios';

import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { DeleteButton, ViewButton } from '@src/components/buttons';
import UpdatePriceSlotNurse from '@src/components/forms/updatePriceSlotNurse';
import { useAuth } from '@src/contexts/authContext';
import { formatShortDate } from '@src/utils';

const url = `${process.env.REACT_APP_API_URL}`;

export default function ClinicListingNurse() {
  const navigate = useNavigate();

  const getInitialPage = (branchId) => {
    const storedBranch = sessionStorage.getItem('nurseListingBranchId');
    if (storedBranch !== branchId) return 0;

    const storedPage = sessionStorage.getItem('nurseListingPage');
    return storedPage ? parseInt(storedPage, 10) : 0;
  };

  const getInitialRowsPerPage = (branchId) => {
    const storedBranch = sessionStorage.getItem('nurseListingBranchId');
    if (storedBranch !== branchId) return 10;

    const storedRows = sessionStorage.getItem('nurseListingRowsPerPage');
    return storedRows ? parseInt(storedRows, 10) : 10;
  };

  const { clinicId } = useParams();
  const branchId = clinicId || '';

  const [page, setPage] = useState(() => getInitialPage(branchId));
  const [rowsPerPage, setRowsPerPage] = useState(() =>
    getInitialRowsPerPage(branchId),
  );

  useEffect(() => {
    sessionStorage.setItem('nurseListingPage', page);
    sessionStorage.setItem('nurseListingBranchId', branchId);
  }, [page, branchId]);

  useEffect(() => {
    sessionStorage.setItem('nurseListingRowsPerPage', rowsPerPage);
    sessionStorage.setItem('nurseListingBranchId', branchId);
  }, [rowsPerPage, branchId]);

  const handleClickClinic = (jobsId) => {
    sessionStorage.setItem('nurseListingPage', page);
    sessionStorage.setItem('nurseListingRowsPerPage', rowsPerPage);
    sessionStorage.setItem('nurseListingBranchId', branchId);
    navigate(`/view-listing-detail-nurse/${jobsId}`);
  };

  const [list, setList] = useState([]);
  const [rows, setRows] = useState([]);
  const { auth } = useAuth();

  const [deleteStatus, setDeleteStatus] = useState();
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortedList, setSortedList] = useState([]);

  useEffect(() => {
    const filteredList = list.filter((row) => {
      return (
        row?.code?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
        row?.date_format?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
        row?.status_by_clinic
          ?.toLowerCase()
          .includes(searchQuery?.toLowerCase())
      );
    });
    const sortedData = [...filteredList].sort((a, b) => {
      const dateA = new Date(a.date_format).getTime();
      const dateB = new Date(b.date_format).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    setSortedList(sortedData);
  }, [list, searchQuery, sortOrder]);

  const handleSortToggle = () => {
    setSortOrder((prevOrder) => (prevOrder === 'desc' ? 'asc' : 'desc'));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [loading, setLoading] = useState();

  const getData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${url}/admin/jobs/search-all-slots?clinicId=${clinicId}&profession=nurse&limit=${list.length}`,
        {
          headers: {
            Authorization: `Bearer ${auth?.idToken}`,
          },
        },
      );

      setList(response.data.data);
      setRows(response.data.totalRows);
      setLoading(false);
    } catch (error) {
      console.log('Error', error.message);
      setLoading(false);
    }
  };
  useEffect(() => {
    if (auth?.idToken) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, deleteStatus]);

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${url}/admin/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
          'My-Custom-Header': 'foobar',
        },
      });
      console.log(response);
      setDeleteStatus('Delete successful');
      toast.success('Slot deleted successfully', {
        position: 'top-center',
        autoClose: 2000,
      });
    } catch (error) {
      console.error(error);
      toast.error(`Error: ${error.response.data.message}`, {
        position: 'top-center',
        autoClose: 2000,
      });
    }
  };

  const showDeleteConfirmationDialog = (id) => {
    setDeleteId(id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmationDialogClose = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmationDialogConfirm = () => {
    handleDelete(deleteId);
    setShowDeleteConfirmation(false);
  };

  const [selected, setSelected] = useState([]);

  const handleRowSelect = (rowId) => {
    setSelected((prevSelected) => {
      const currentPageSelection = prevSelected[page] || [];
      const isSelected = currentPageSelection.includes(rowId);

      return {
        ...prevSelected,
        [page]: isSelected
          ? currentPageSelection.filter((id) => id !== rowId)
          : [...currentPageSelection, rowId],
      };
    });
  };

  const handleSelectAll = () => {
    const currentPageIds = sortedList
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row) => row._id);

    setSelected((prevSelected) => {
      const currentPageSelection = prevSelected[page] || [];
      const allSelected = currentPageSelection.length === currentPageIds.length;

      return {
        ...prevSelected,
        [page]: allSelected ? [] : currentPageIds,
      };
    });
  };

  return (
    <div className='px-2'>
      <Box className='flex justify-between py-4'>
        <TextField
          fullWidth
          size='medium'
          variant='outlined'
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              height: '50px',
            },
            mt: '5px',
            width: '250px',
          }}
          onChange={handleSearch}
          placeholder='Search by ID or date'
        />

        <Box className='flex w-3/4 justify-start px-3'>
          <Select
            fullWidth
            displayEmpty
            sx={{
              height: '50px',
              backgroundColor: 'white',
              mt: 0.5,
              mb: '16px',
              borderRadius: 2,
              width: '150px',
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                style: {
                  zIndex: 1302,
                },
              },
            }}
            value={searchQuery}
            onChange={handleSearch}
          >
            <MenuItem disabled value=''>
              <em style={{ color: '#8C8C8C' }}>Select</em>
            </MenuItem>

            <MenuItem value=''>ALL</MenuItem>
            <MenuItem value='empty'>Empty</MenuItem>
            <MenuItem value='pending'>Pending</MenuItem>
            <MenuItem value='filled'>Filled</MenuItem>
            <MenuItem value='past'>Past</MenuItem>
            <MenuItem value='missed'>Missed</MenuItem>
          </Select>
          &nbsp; &nbsp;
          <Button
            variant='contained'
            className='rounded-md bg-cyan-600 py-2 text-sm font-bold'
            sx={{
              width: '80px',
              height: '38px',
              textTransform: 'none',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
              mt: 1,
            }}
            onClick={clearSearch}
          >
            Clear
          </Button>
        </Box>

        <Box>
          {Object.values(selected).flat().length > 0 && (
            <UpdatePriceSlotNurse selected={selected} getData={getData} />
          )}
        </Box>
      </Box>
      <br />
      {loading ? (
        <div className='font-poppins py-6 px-8 text-gray-500'>Loading...</div>
      ) : (
        <span className='flex flex-col items-start'>
          <TableContainer
            className='rounded-xl'
            component={Paper}
            style={{
              width: '100%',
              paddingBottom: '12px',
              paddingLeft: '12px',
              paddingRight: '12px',
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell style={{ padding: 0, width: '10px' }}>
                    <Checkbox
                      style={{ color: '#1788b9' }}
                      checked={
                        (selected[page] || []).length ===
                        sortedList.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        ).length
                      }
                      indeterminate={
                        (selected[page] || []).length > 0 &&
                        (selected[page] || []).length <
                          sortedList.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          ).length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>

                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ width: '90px' }}
                  >
                    Listing ID
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 0, width: '80px' }}
                  >
                    Price/hour
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ width: '100px' }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 0, width: '150px' }}
                  >
                    <TableSortLabel
                      active
                      direction={sortOrder}
                      onClick={handleSortToggle}
                      className='text-xs font-bold text-gray-400'
                      sx={{ width: '50px' }}
                    >
                      Application Date/Time
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ width: '40px' }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ width: '40px' }}
                  >
                    Completed
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 4, width: '120px' }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <br />
              <TableBody>
                {Array.isArray(sortedList) && sortedList.length > 0 ? (
                  sortedList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        hover
                        role='checkbox'
                        tabIndex={-1}
                        key={row._id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <div className='py-2'>
                          <Checkbox
                            style={{ color: '#1788b9' }}
                            checked={(selected[page] || []).includes(row._id)}
                            onChange={() => handleRowSelect(row._id)}
                          />
                        </div>
                        <TableCell className='border-white text-sm font-bold text-gray-500'>
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                            }}
                          >
                            <div>{row?.code}</div>
                          </span>
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 0 }}
                        >
                          ${row?.price} SGD
                        </TableCell>
                        <TableCell className='border-white text-sm font-bold text-gray-500'>
                          {row?.type
                            ? row.type
                                .replace(/_/g, ' ')
                                .split(' ')
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1),
                                )
                                .join(' ')
                            : ''}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 0 }}
                        >
                          <div>
                            {formatShortDate(row?.date_format)}
                            <div>
                              {row?.time_start_format} -{' '}
                              {row?.time_finish_format}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className='border-white text-sm font-bold text-gray-500'>
                          {row?.status_by_clinic}
                        </TableCell>
                        <TableCell className='border-white text-sm font-bold text-gray-500'>
                          {row?.completed ? 'true' : 'false'}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 0 }}
                        >
                          <ViewButton
                            onClick={() => handleClickClinic(row?._id)}
                          />
                          &nbsp; &nbsp;
                          <DeleteButton
                            onClick={() =>
                              showDeleteConfirmationDialog(row?._id)
                            }
                          />
                          {showDeleteConfirmation && (
                            <Dialog
                              open={showDeleteConfirmation}
                              onClose={handleConfirmationDialogClose}
                            >
                              <DialogTitle>Confirm Delete</DialogTitle>
                              <DialogContent>
                                <DialogContentText>
                                  Are you sure you want to delete this slot?
                                </DialogContentText>
                              </DialogContent>
                              <DialogActions>
                                <Button
                                  onClick={handleConfirmationDialogClose}
                                  color='primary'
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleConfirmationDialogConfirm}
                                  color='error'
                                >
                                  Delete
                                </Button>
                              </DialogActions>
                            </Dialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      className='border border-white'
                      colSpan={6}
                      align='center'
                    >
                      <div className='font-poppins py-6 text-gray-500'>
                        No data
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box
              className='flex justify-end py-3'
              sx={{ width: '100%', fontFamily: 'Poppins' }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component='div'
                count={sortedList.length}
                // count={rows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </TableContainer>
        </span>
      )}
    </div>
  );
}
